import React, { useState, useEffect, useRef } from "react";
import "swiper/css";
import "swiper/css/navigation";
import './AnimatedSlider.css';
import axios from 'axios';
import config from './config.json';

// Shuffle array function
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// PowerPoint-style transition effects
const EFFECTS = [
  'effect-fade',
  'effect-zoom-in',
  'effect-zoom-out',
  'effect-slide-left',
  'effect-slide-right',
  'effect-slide-top',
  'effect-slide-bottom',
  'effect-flip-h',
  'effect-flip-v',
  'effect-diamond',
  'effect-explode',
  'effect-shutter',
  'effect-push',
  'effect-wipe',
  'effect-curtain',
  'effect-blur',
];

function getRandomEffect() {
  return EFFECTS[Math.floor(Math.random() * EFFECTS.length)];
}

export default function AnimatedSlider({ interval = 3500 }) {
  const [galleryImages, setGalleryImages] = useState([]);
  const [index, setIndex] = useState(0);
  const [effect, setEffect] = useState(getRandomEffect());
  const [loaded, setLoaded] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetchSliderImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (galleryImages.length > 0) {
      startTimer();
    }
    return () => stopTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, galleryImages.length]);

  const fetchSliderImages = async () => {
    try {
      setLoaded(false);
      const fileName = 'images/slider/';
      const bucketName = 'images.mduschooled.in';
      const response = await axios.get(
        `${config.api.invokeURL}/website/gallery/slider?fileName=${fileName}&bucketName=${bucketName}`,
        {
          headers: {
            'x-api-key': config.api.key,
          },
        }
      );

      const images = Array.isArray(response.data) ? response.data : [];
      setGalleryImages(shuffleArray(images));
    } catch (error) {
      console.error('Error fetching slider images:', error);
    } finally {
      setLoaded(true);
    }
  };

  function startTimer() {
    stopTimer();
    timeoutRef.current = setTimeout(() => {
      setEffect(getRandomEffect());
      setIndex((prev) => (galleryImages.length ? (prev + 1) % galleryImages.length : 0));
    }, interval);
  }

  function stopTimer() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }

  function getImageUrl(img) {
    if (!img) return '';
    if (typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://'))) {
      return img;
    }
    return `${config.s3.imageUrl}${img}`;
  }

  function goTo(i) {
    setEffect(getRandomEffect());
    setIndex(galleryImages.length ? i % galleryImages.length : 0);
  }

  function prev() {
    if (!galleryImages.length) return;
    setEffect(getRandomEffect());
    setIndex((prevIdx) => (prevIdx - 1 + galleryImages.length) % galleryImages.length);
  }

  function next() {
    if (!galleryImages.length) return;
    setEffect(getRandomEffect());
    setIndex((prevIdx) => (prevIdx + 1) % galleryImages.length);
  }

  if (!loaded) {
    return <div className="animated-slider-loading">Loading gallery...</div>;
  }

  if (galleryImages.length === 0) {
    return <div className="animated-slider-empty">No gallery images found.</div>;
  }

  return (
    <section id="gallery" className="container-fluid p-0">
      <div className="ag-container animated-gallery">
        {galleryImages.map((img, i) => (
          <div
            key={i}
            className={`ag-slide ${i === index ? `active ${effect}` : ''}`}
            style={{ backgroundImage: `url(${getImageUrl(img)})`, height: '100%' }}
            aria-hidden={i === index ? 'false' : 'true'}
          />
        ))}

        <button className="ag-arrow left" onClick={() => { stopTimer(); prev(); }} aria-label="Previous slide">‹</button>
        <button className="ag-arrow right" onClick={() => { stopTimer(); next(); }} aria-label="Next slide">›</button>

        <div className="ag-controls" role="tablist" aria-label="Gallery dots">
          {galleryImages.map((_, i) => (
            <div
              key={i}
              role="tab"
              aria-selected={i === index}
              tabIndex={0}
              className={`ag-dot ${i === index ? 'active' : ''}`}
              onClick={() => { stopTimer(); goTo(i); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { stopTimer(); goTo(i); } }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
